/**
 * The split screen's right panel (ONBOARDING-FLOW-SPEC.md S0, §2): the
 * merchant's own Executive Overview assembling live, reusing the exact
 * dashboard widget components pages/Dashboards.tsx renders with. Honesty
 * rule is absolute: every number here is either a real warehouse value or
 * a skeleton — nothing is ever invented, including a "reasonable-looking"
 * placeholder. Every answer that arms a new pinned card queries for real
 * data immediately; until that resolves, the card shows a skeleton.
 */

import { useEffect, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Card,
  InlineGrid,
  SkeletonBodyText,
  Text,
} from '@shopify/polaris';

import {
  askStructured,
  getDashboardData,
  listDashboards,
} from '../../services/marketingSuiteApi';
import type {
  Dashboard,
  DashboardData,
  MetricResult,
  WidgetData,
} from '../../types/marketingSuite';
import { BarChart, KpiValue, LineChart } from '../marketingSuite/MiniChart';
import { MetricTable } from '../marketingSuite/MetricTable';
import { GOAL_HERO_METRIC } from './treeWalk';

function isResult(data: WidgetData | undefined): data is MetricResult {
  return !!data && !('error' in data);
}

type HeroState = MetricResult | 'loading' | 'unavailable';

interface RightPanelProps {
  goal: string | null;
  hasBudget: boolean;
  competitors: string[] | null;
  /** Bump to force a reload — e.g. an OAuth connect just completed. */
  refreshToken: number;
}

export function RightPanel({
  goal,
  hasBudget,
  competitors,
  refreshToken,
}: RightPanelProps) {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [hero, setHero] = useState<HeroState>('loading');

  useEffect(() => {
    let cancelled = false;
    listDashboards()
      .then((list) => {
        const active = list.find((d) => d.is_default) ?? list[0] ?? null;
        if (cancelled) return null;
        setDashboard(active);
        setDashboardError(null);
        return active ? getDashboardData(active.id) : null;
      })
      .then((resolved) => {
        if (!cancelled && resolved) setData(resolved);
      })
      .catch((e) => {
        if (!cancelled) {
          setDashboardError(
            e instanceof Error ? e.message : 'Failed to load your dashboard'
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  useEffect(() => {
    const metric = goal ? GOAL_HERO_METRIC[goal] : undefined;
    if (!metric) {
      setHero('unavailable');
      return;
    }
    let cancelled = false;
    setHero('loading');
    askStructured({ metric })
      .then((response) => {
        if (!cancelled) setHero(response.result);
      })
      .catch(() => {
        if (!cancelled) setHero('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [goal, refreshToken]);

  return (
    <BlockStack gap="300">
      <Text as="h2" variant="headingMd">
        Your Executive Overview
      </Text>
      {dashboardError && <Banner tone="warning">{dashboardError}</Banner>}

      {goal && (
        <Card>
          <BlockStack gap="100">
            <Text as="h3" variant="headingSm" tone="subdued">
              Goal
            </Text>
            {hero === 'loading' ? (
              <SkeletonBodyText lines={1} />
            ) : hero === 'unavailable' || hero.rows.length === 0 ? (
              <Text as="p" tone="subdued">
                Waiting for data to sync…
              </Text>
            ) : (
              <KpiValue
                rows={hero.rows}
                unit={hero.unit}
                directionIsImprovement={hero.direction_is_improvement}
              />
            )}
          </BlockStack>
        </Card>
      )}

      {hasBudget && (
        <Card>
          <BlockStack gap="100">
            <Text as="h3" variant="headingSm" tone="subdued">
              Pacing
            </Text>
            <SkeletonBodyText lines={1} />
            <Text as="p" tone="subdued">
              Pacing appears once your ad spend syncs.
            </Text>
          </BlockStack>
        </Card>
      )}

      {competitors && competitors.length > 0 && (
        <Card>
          <BlockStack gap="100">
            <Text as="h3" variant="headingSm" tone="subdued">
              Competitor watch
            </Text>
            <Text as="p">{competitors.join(', ')}</Text>
            <Text as="p" tone="subdued">
              Your first digest arrives after this week&apos;s sweep.
            </Text>
          </BlockStack>
        </Card>
      )}

      {!dashboard ? (
        <Card>
          <SkeletonBodyText lines={6} />
        </Card>
      ) : (
        <BlockStack gap="300">
          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
            {dashboard.widgets
              .filter((w) => w.widget_type === 'kpi')
              .map((w) => {
                const wd = data?.widgets[w.id];
                return (
                  <Card key={w.id}>
                    <BlockStack gap="100">
                      <Text as="h3" variant="headingSm" tone="subdued">
                        {w.title}
                      </Text>
                      {isResult(wd) ? (
                        <KpiValue
                          rows={wd.rows}
                          unit={wd.unit}
                          directionIsImprovement={wd.direction_is_improvement}
                        />
                      ) : wd ? (
                        <Badge tone="attention">
                          {('message' in wd && wd.message) || wd.error}
                        </Badge>
                      ) : (
                        <SkeletonBodyText lines={1} />
                      )}
                    </BlockStack>
                  </Card>
                );
              })}
          </InlineGrid>
          <BlockStack gap="300">
            {dashboard.widgets
              .filter((w) => w.widget_type !== 'kpi')
              .map((w) => {
                const wd = data?.widgets[w.id];
                return (
                  <Card key={w.id}>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingSm">
                        {w.title}
                      </Text>
                      {isResult(wd) ? (
                        w.widget_type === 'breakdown' ? (
                          <BarChart rows={wd.rows} unit={wd.unit} />
                        ) : w.widget_type === 'table' ? (
                          <MetricTable rows={wd.rows} unit={wd.unit} />
                        ) : (
                          <LineChart rows={wd.rows} unit={wd.unit} />
                        )
                      ) : wd ? (
                        <Text as="p" tone="subdued">
                          {('message' in wd && wd.message) ||
                            'No data for this widget yet.'}
                        </Text>
                      ) : (
                        <SkeletonBodyText lines={4} />
                      )}
                    </BlockStack>
                  </Card>
                );
              })}
          </BlockStack>
        </BlockStack>
      )}
    </BlockStack>
  );
}
